/**
 * Module dependencies.
 */

import { Dispatch, ReactElement, SetStateAction, useCallback } from 'react';
import { Radio } from 'src/components/core';
import { StepPageConfig } from 'src/types/advisor';
import { balloonsOptions } from 'src/core/advisor-config/steps-options';
import { units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import BaseStep from './base-step';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = StepPageConfig & {
  balloonSize: number;
  className?: string;
  onSetBalloonSize: Dispatch<SetStateAction<number>>;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

/**
 * `OptionsWrapper` styled component.
 */

const OptionsWrapper = styled.div`
  display: grid;
  grid-auto-columns: 100%;
  grid-row-gap: ${units(3)};
  margin-bottom: ${units(6)};
`;

/**
 * `StyledRadio` styled component.
 */

const StyledRadio = styled(Radio)`
  font-weight: 700;
`;

/**
 * `MockedBalloonsStep` component.
 */

const MockedBalloonsStep = (props: Props): ReactElement => {
  const { balloonSize, className, onSetBalloonSize, pageIndex, pageKey } =
    props;

  const { onGoToNext, onGoToPrevious } = useAdvisor();
  const handleChange = useCallback(
    ({ target }) => {
      onSetBalloonSize(Number(target.value));
      onGoToNext(target.value, pageKey, pageIndex);
    },
    [onGoToNext, onSetBalloonSize, pageIndex, pageKey]
  );

  return (
    <Wrapper className={className}>
      <BaseStep
        onContinue={() => onGoToNext(`${balloonSize}`, pageKey, pageIndex)}
        onGoBack={() => onGoToPrevious(pageIndex)}
        pageKey={pageKey}
        {...props}
      >
        <OptionsWrapper>
          {map(balloonsOptions, (option, index) => (
            <StyledRadio
              checked={balloonSize === option}
              key={index}
              label={option}
              name={`advisor-${pageKey}`}
              onChange={handleChange}
              value={option}
            />
          ))}
        </OptionsWrapper>
      </BaseStep>
    </Wrapper>
  );
};

/**
 * Export `MockedBalloonsStep` component.
 */

export default MockedBalloonsStep;
